import { useState, useEffect } from "react";
import { authFetch } from "../lib/authFetch";
import { usePortailRouter } from "../lib/router-context";
import { ArrowLeft, Loader2, AlertCircle, CheckCircle2, XCircle, Package } from "lucide-react";

interface DevisData {
  id: string;
  numero: string;
  statut: string;
  societe: string | null;
  contactNom: string | null;
  clientFinalNom: string | null;
  objet: string | null;
  notes: string | null;
  adresse: string | null;
  lignes: any[];
  remiseGlobale: string | null;
  createdAt: string;
  typeDemande: string | null;
  environnement: string | null;
  dateLivraisonSouhaitee: string | null;
}

const TYPE_LABELS: Record<string, { label: string; className: string }> = {
  achat: { label: "Achat", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  location: { label: "Location", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
  mixte: { label: "Mixte", className: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
};

const STATUT_LABELS: Record<string, string> = {
  brouillon: "Brouillon",
  envoye: "Envoyé",
  accepte: "Accepté",
  refuse: "Refusé",
  expire: "Expiré",
};

const STATUT_CLASSES: Record<string, string> = {
  brouillon: "bg-muted text-muted-foreground",
  envoye: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  accepte: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  refuse: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  expire: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

function formatPrix(n: number | null | undefined) {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export default function DevisDetailPage({ id }: { id: string }) {
  const { navigate } = usePortailRouter();
  const [devis, setDevis] = useState<DevisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authFetch(`/api/portail/devis/${id}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error("Devis introuvable")))
      .then(data => { if (!cancelled) setDevis(data); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );

  if (error || !devis) return (
    <div className="flex items-center justify-center py-24 flex-col gap-3">
      <AlertCircle className="w-10 h-10 text-destructive" />
      <p className="text-destructive">{error || "Devis introuvable"}</p>
    </div>
  );

  const lignes = devis.lignes || [];
  const sousTotal = lignes.reduce((sum, l) => {
    const periods = l.typeLigne === 'location' ? (Number(l.nbPeriodes) || 1) : 1;
    return sum + (Number(l.puHt) || 0) * (Number(l.quantite) || 1) * periods * (1 - (Number(l.remise) || 0) / 100);
  }, 0);
  const remiseGlobale = Number(devis.remiseGlobale) || 0;
  const totalHT = sousTotal * (1 - remiseGlobale / 100);
  const tva = totalHT * 0.2;
  const ttc = totalHT + tva;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="px-6 pt-6 pb-4 border-b border-border/60 bg-card sticky top-0 z-10">
        <button onClick={() => navigate("/devis")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="button-back-devis">
          <ArrowLeft className="w-4 h-4" /> Retour aux devis
        </button>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-foreground">{devis.numero}</h1>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_CLASSES[devis.statut] || "bg-muted text-muted-foreground"}`}>
            {STATUT_LABELS[devis.statut] || devis.statut}
          </span>
          {devis.typeDemande && TYPE_LABELS[devis.typeDemande] && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_LABELS[devis.typeDemande].className}`}>
              {TYPE_LABELS[devis.typeDemande].label}
            </span>
          )}
        </div>
        {devis.objet && <p className="text-sm text-muted-foreground mt-0.5">{devis.objet}</p>}
      </div>

      <div className="p-6 space-y-6">
        {devis.statut === "accepte" && (
          <div className="flex items-start gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
            <p className="text-sm text-green-800 dark:text-green-200">
              Ce devis a été accepté. Notre équipe vous contactera prochainement pour la suite.
            </p>
          </div>
        )}
        {devis.statut === "refuse" && (
          <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-200">
              Ce devis a été refusé. Contactez votre interlocuteur Jobotto pour plus d'informations.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border/50 rounded-xl p-4 space-y-2">
            <h3 className="text-sm font-semibold text-foreground mb-3">Informations</h3>
            {devis.societe && <p className="text-sm"><span className="text-muted-foreground">Société : </span>{devis.societe}</p>}
            {devis.contactNom && <p className="text-sm"><span className="text-muted-foreground">Contact : </span>{devis.contactNom}</p>}
            {devis.clientFinalNom && <p className="text-sm"><span className="text-muted-foreground">Client final : </span>{devis.clientFinalNom}</p>}
            {devis.objet && <p className="text-sm"><span className="text-muted-foreground">Objet : </span>{devis.objet}</p>}
            {devis.environnement && <p className="text-sm"><span className="text-muted-foreground">Environnement : </span>{devis.environnement}</p>}
            {devis.dateLivraisonSouhaitee && <p className="text-sm"><span className="text-muted-foreground">Livraison souhaitée : </span>{new Date(devis.dateLivraisonSouhaitee).toLocaleDateString("fr-FR")}</p>}
          </div>
          <div className="bg-card border border-border/50 rounded-xl p-4 space-y-2">
            <h3 className="text-sm font-semibold text-foreground mb-3">Récapitulatif financier</h3>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Sous-total HT</span><span className="font-medium">{formatPrix(sousTotal)}</span></div>
              {remiseGlobale > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Remise globale</span><span className="text-red-600">−{remiseGlobale}%</span></div>}
              <div className="flex justify-between border-t border-border pt-1.5"><span className="text-muted-foreground">Total HT</span><span className="font-semibold">{formatPrix(totalHT)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">TVA 20%</span><span>{formatPrix(tva)}</span></div>
              <div className="flex justify-between border-t border-border pt-1.5 text-base font-bold"><span>Net TTC</span><span>{formatPrix(ttc)}</span></div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border/60 bg-muted/30">
            <h3 className="text-sm font-semibold text-foreground">Produits ({lignes.length})</h3>
          </div>
          {lignes.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Package className="w-8 h-8 mr-2 opacity-30" /> Aucun produit
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/40">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Produit</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Prix HT</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Qté</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Remise</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Total HT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {lignes.map((l, i) => {
                  const prix = Number(l.puHt) || 0;
                  const qty = Number(l.quantite) || 1;
                  const remise = Number(l.remise) || 0;
                  const periods = l.typeLigne === 'location' ? (Number(l.nbPeriodes) || 1) : 1;
                  const total = prix * qty * periods * (1 - remise / 100);
                  return (
                    <tr key={l.id || i}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {l.imageUrl && (
                            <img src={l.imageUrl} alt={l.nomFr} className="w-10 h-10 object-contain rounded border border-border flex-shrink-0" />
                          )}
                          <div>
                            <p className="font-medium text-foreground">{l.nomFr}</p>
                            {l.sku && <p className="text-xs text-muted-foreground font-mono">{l.sku}</p>}
                            {l.typeLigne === 'location' && l.dureeLocation && (
                              <p className="text-xs text-emerald-600 dark:text-emerald-400">Location — {l.dureeLocation === 'jour' ? 'Journalier' : l.dureeLocation === 'semaine' ? 'Hebdomadaire' : 'Mensuel'}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatPrix(prix)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{qty}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{remise > 0 ? `−${remise}%` : "—"}</td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums">{formatPrix(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {devis.adresse && (
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-2">Adresse de livraison</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{devis.adresse}</p>
          </div>
        )}
        {devis.notes && (
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-2">Notes</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{devis.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
